import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency, formatDate } from "@/lib/api";
import { Plus, TrendingUp, CheckCircle, XCircle, Clock, FileText, Truck, Upload, Send, ClipboardCheck, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";

function QuoteForm({ onAdd, onCancel }) {
  const [q, setQ] = useState({
    supplier_name: "", supplier_contact: "", bbee_level: "", tax_clearance: false,
    cost_ex_vat: 0, vat_amount: 0, cost_incl_vat: 0, lead_time_days: 0,
    warranty_period: "", warranty_terms: "", payment_terms: "", validity_days: 30,
    sureties: "", notes: "", is_recommended: false
  });

  const calcVat = (exVat) => {
    const vat = Math.round(exVat * 0.15 * 100) / 100;
    setQ({ ...q, cost_ex_vat: exVat, vat_amount: vat, cost_incl_vat: exVat + vat });
  };

  return (
    <div className="stratum-card p-5 border-l-4 border-l-[#C8A961]">
      <h4 className="font-display text-base font-semibold mb-4">Add Supplier Quote</h4>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Supplier Name *</label>
          <input className="stratum-input w-full" value={q.supplier_name} onChange={e => setQ({...q, supplier_name: e.target.value})} data-testid="quote-supplier-name" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Contact</label>
          <input className="stratum-input w-full" value={q.supplier_contact} onChange={e => setQ({...q, supplier_contact: e.target.value})} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Cost Excl. VAT (R) *</label>
          <input type="number" className="stratum-input w-full font-mono" value={q.cost_ex_vat} onChange={e => calcVat(parseFloat(e.target.value) || 0)} data-testid="quote-cost-ex-vat" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">VAT (15%)</label>
          <input type="number" className="stratum-input w-full font-mono bg-[#FAF8F4]" value={q.vat_amount} readOnly />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Cost Incl. VAT (R)</label>
          <input type="number" className="stratum-input w-full font-mono bg-[#FAF8F4] font-semibold" value={q.cost_incl_vat} readOnly />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Lead Time (days)</label>
          <input type="number" className="stratum-input w-full font-mono" value={q.lead_time_days} onChange={e => setQ({...q, lead_time_days: parseInt(e.target.value) || 0})} data-testid="quote-lead-time" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">B-BBEE Level</label>
          <select className="stratum-input w-full" value={q.bbee_level} onChange={e => setQ({...q, bbee_level: e.target.value})}>
            <option value="">N/A</option>
            {[1,2,3,4,5,6,7,8].map(l => <option key={l} value={`Level ${l}`}>Level {l}</option>)}
            <option value="Non-compliant">Non-compliant</option>
          </select>
        </div>
        <div className="flex items-center gap-2 pt-5">
          <input type="checkbox" checked={q.tax_clearance} onChange={e => setQ({...q, tax_clearance: e.target.checked})} className="rounded" />
          <span className="text-sm">Tax clearance valid</span>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Warranty Period</label>
          <input className="stratum-input w-full" value={q.warranty_period} onChange={e => setQ({...q, warranty_period: e.target.value})} placeholder="e.g. 24 months" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Warranty Terms</label>
          <input className="stratum-input w-full" value={q.warranty_terms} onChange={e => setQ({...q, warranty_terms: e.target.value})} placeholder="Parts & labour" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Payment Terms</label>
          <input className="stratum-input w-full" value={q.payment_terms} onChange={e => setQ({...q, payment_terms: e.target.value})} placeholder="e.g. 30 days from invoice" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Sureties / Guarantees</label>
          <input className="stratum-input w-full" value={q.sureties} onChange={e => setQ({...q, sureties: e.target.value})} placeholder="e.g. Performance bond 10%" />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Additional Notes</label>
          <textarea className="stratum-input w-full" rows={2} value={q.notes} onChange={e => setQ({...q, notes: e.target.value})} />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={q.is_recommended} onChange={e => setQ({...q, is_recommended: e.target.checked})} className="rounded" />
          <span className="text-sm font-medium text-[#C8A961]">Recommended supplier</span>
        </div>
      </div>
      <div className="flex gap-3 mt-4">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm">Cancel</button>
        <button onClick={() => onAdd(q)} className="stratum-btn-primary" data-testid="save-quote-btn" disabled={!q.supplier_name || !q.cost_ex_vat}>Add Quote</button>
      </div>
    </div>
  );
}

function DeliveryChecklist({ checklist, onUpdate, projectStatus }) {
  const items = [
    { key: "asset_physically_received", label: "Asset physically received at site" },
    { key: "asset_inspected_condition_ok", label: "Asset inspected - condition OK" },
    { key: "serial_numbers_recorded", label: "Serial numbers / identifiers recorded" },
    { key: "location_assigned", label: "Location assigned" },
    { key: "insurance_in_place", label: "Insurance in place" },
    { key: "custodian_assigned", label: "Custodian assigned" },
    { key: "qr_label_attached", label: "QR / asset label attached" },
    { key: "available_for_use", label: "Asset available for intended use" },
  ];

  const allDone = items.every(i => checklist[i.key]);

  return (
    <div className="stratum-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardCheck size={18} className="text-[#C8A961]" />
        <h4 className="font-display text-base font-semibold">Delivery & Commissioning Checklist</h4>
      </div>
      <div className="space-y-2">
        {items.map(item => (
          <label key={item.key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#FAF8F4] cursor-pointer">
            <input
              type="checkbox"
              checked={checklist[item.key] || false}
              onChange={e => {
                const updated = { ...checklist, [item.key]: e.target.checked };
                onUpdate(updated);
              }}
              className="rounded w-4 h-4"
              disabled={projectStatus !== "APPROVED"}
              data-testid={`checklist-${item.key}`}
            />
            <span className={`text-sm ${checklist[item.key] ? "text-[#065F46] line-through" : "text-[#1A1A1A]"}`}>{item.label}</span>
          </label>
        ))}
      </div>
      {checklist.signed_off_by && (
        <div className="mt-3 p-2 rounded-lg bg-[#065F46]/10 text-[#065F46] text-xs">
          Signed off at {formatDate(checklist.signed_off_at)}
        </div>
      )}
      {allDone && !checklist.signed_off_by && (
        <div className="mt-3 p-2 rounded-lg bg-[#C8A961]/10 text-[#C8A961] text-xs font-medium">
          All items checked - ready to capitalise
        </div>
      )}
    </div>
  );
}

export default function CapexPage() {
  const { activeEntity, getCategoryName, categories, entities } = useApp();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", entity_id: "", category_id: "",
    estimated_cost: 0, rationale: "", strategic_objective: "",
    estimated_delivery_days: 0, warranty_terms: "", sureties: "",
    payment_terms: "", other_terms: ""
  });

  const loadProjects = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeEntity) params.entity_id = activeEntity.id;
      const { data } = await api.get("/capex", { params });
      setProjects(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadProjects(); }, [activeEntity]);

  const createProject = async () => {
    try {
      const payload = { ...form, entity_id: form.entity_id || activeEntity?.id };
      await api.post("/capex", payload);
      setShowForm(false);
      setForm({ name: "", description: "", entity_id: "", category_id: "", estimated_cost: 0, rationale: "", strategic_objective: "", estimated_delivery_days: 0, warranty_terms: "", sureties: "", payment_terms: "", other_terms: "" });
      loadProjects();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const addQuote = async (projectId, quote) => {
    try {
      await api.post(`/capex/${projectId}/quotes`, quote);
      setShowQuoteForm(null);
      loadProjects();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const selectSupplier = async (projectId, quoteIndex, rationale) => {
    try {
      await api.post(`/capex/${projectId}/select-supplier`, { quote_index: quoteIndex, rationale });
      loadProjects();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const approveProject = async (projectId) => {
    if (!window.confirm("Approve this capex project and issue PO?")) return;
    try {
      const { data } = await api.post(`/capex/${projectId}/approve`, { decision: "APPROVED", comment: "" });
      alert(`Project approved!\nPO Number: ${data.po_number || "N/A"}`);
      loadProjects();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const updateChecklist = async (projectId, checklist) => {
    try {
      await api.put(`/capex/${projectId}/checklist`, { delivery_checklist: checklist });
      loadProjects();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const capitaliseProject = async (projectId) => {
    if (!window.confirm("Capitalise this project and create a depreciating asset?")) return;
    try {
      const { data } = await api.post(`/capex/${projectId}/capitalise`, {});
      alert(`Asset created: ${data.asset.tag}\nJournal: ${data.journal.reference}\nMonthly depreciation: ${formatCurrency(data.asset.monthly_depreciation)}`);
      loadProjects();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const statusBadge = (status) => {
    const colors = {
      DRAFT: "bg-[#6B7280]/10 text-[#6B7280]",
      APPROVED: "bg-[#065F46]/10 text-[#065F46]",
      REJECTED: "bg-[#991B1B]/10 text-[#991B1B]",
      CAPITALISED: "bg-[#C8A961]/10 text-[#C8A961]",
    };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.DRAFT}`}>{status}</span>;
  };

  return (
    <div className="space-y-6" data-testid="capex-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">Capex / WIP</h1>
          <p className="text-sm text-[#6B7280] mt-1">Capital expenditure requests with supplier quotes, approval, PO, and delivery checklist</p>
        </div>
        <button onClick={() => setShowForm(true)} className="stratum-btn-primary flex items-center gap-2" data-testid="new-capex-btn">
          <Plus size={16} /> New Capex Request
        </button>
      </div>

      {/* New Capex Form */}
      {showForm && (
        <div className="stratum-card p-6">
          <h3 className="font-display text-lg font-semibold mb-4">New Capex Request</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Project Name *</label>
                <input className="stratum-input w-full" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="capex-name-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Entity</label>
                <select className="stratum-input w-full" value={form.entity_id || activeEntity?.id || ""} onChange={e => setForm({...form, entity_id: e.target.value})}>
                  {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Description</label>
              <textarea className="stratum-input w-full" rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="capex-desc-input" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Category *</label>
                <select className="stratum-input w-full" value={form.category_id} onChange={e => setForm({...form, category_id: e.target.value})} data-testid="capex-category-select">
                  <option value="">Select...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Estimated Cost Excl. VAT (R)</label>
                <input type="number" className="stratum-input w-full font-mono" value={form.estimated_cost} onChange={e => setForm({...form, estimated_cost: parseFloat(e.target.value) || 0})} data-testid="capex-cost-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Est. Delivery (days)</label>
                <input type="number" className="stratum-input w-full font-mono" value={form.estimated_delivery_days} onChange={e => setForm({...form, estimated_delivery_days: parseInt(e.target.value) || 0})} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Rationale / Business Case *</label>
              <textarea className="stratum-input w-full" rows={3} value={form.rationale} onChange={e => setForm({...form, rationale: e.target.value})} data-testid="capex-rationale-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Warranty Terms</label>
                <input className="stratum-input w-full" value={form.warranty_terms} onChange={e => setForm({...form, warranty_terms: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Sureties / Guarantees</label>
                <input className="stratum-input w-full" value={form.sureties} onChange={e => setForm({...form, sureties: e.target.value})} />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm">Cancel</button>
              <button onClick={createProject} className="stratum-btn-primary" data-testid="submit-capex-btn" disabled={!form.name || !form.category_id}>Submit Request</button>
            </div>
          </div>
        </div>
      )}

      {/* Project List */}
      <div className="space-y-4">
        {loading ? (
          <div className="stratum-card p-8 text-center text-[#6B7280]">Loading...</div>
        ) : projects.length === 0 ? (
          <div className="stratum-card p-12 text-center">
            <TrendingUp size={32} className="mx-auto text-[#C8A961] mb-3" />
            <p className="text-[#6B7280]">No capex projects yet. Create one to begin the procurement workflow.</p>
          </div>
        ) : projects.map(p => (
          <div key={p.id} className="stratum-card overflow-hidden" data-testid={`capex-row-${p.id}`}>
            {/* Project Header */}
            <div className="p-5 cursor-pointer hover:bg-[#FAF8F4] transition-colors" onClick={() => setExpandedProject(expandedProject === p.id ? null : p.id)}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h4 className="font-medium text-[#0B1020]">{p.name}</h4>
                    {statusBadge(p.status)}
                    {p.po_number && <span className="text-xs font-mono text-[#C8A961] bg-[#C8A961]/10 px-2 py-0.5 rounded">{p.po_number}</span>}
                  </div>
                  <div className="flex gap-6 text-xs text-[#6B7280]">
                    <span>Category: {getCategoryName(p.category_id)}</span>
                    <span className="font-mono tabular-nums">Est: {formatCurrency(p.estimated_cost)}</span>
                    <span>{p.quotes?.length || 0} quote(s)</span>
                    <span>{formatDate(p.created_at)}</span>
                  </div>
                </div>
                {expandedProject === p.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </div>
            </div>

            {/* Expanded Detail */}
            {expandedProject === p.id && (
              <div className="border-t border-[#E8E2D5] p-5 space-y-5 bg-[#FAF8F4]/50">
                {/* Rationale */}
                <div>
                  <h5 className="text-xs font-medium text-[#6B7280] uppercase tracking-wider mb-1">Rationale</h5>
                  <p className="text-sm text-[#1A1A1A]">{p.rationale || "Not provided"}</p>
                </div>

                {/* Terms */}
                {(p.warranty_terms || p.sureties || p.payment_terms) && (
                  <div className="grid grid-cols-3 gap-4">
                    {p.warranty_terms && <div><span className="text-xs text-[#6B7280]">Warranty</span><p className="text-sm">{p.warranty_terms}</p></div>}
                    {p.sureties && <div><span className="text-xs text-[#6B7280]">Sureties</span><p className="text-sm">{p.sureties}</p></div>}
                    {p.payment_terms && <div><span className="text-xs text-[#6B7280]">Payment Terms</span><p className="text-sm">{p.payment_terms}</p></div>}
                  </div>
                )}

                {/* Comparative Quotes */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">Comparative Supplier Quotes</h5>
                    {p.status === "DRAFT" && (
                      <button onClick={() => setShowQuoteForm(p.id)} className="px-3 py-1.5 text-xs rounded-lg border border-[#E8E2D5] hover:bg-white flex items-center gap-1.5" data-testid={`add-quote-${p.id}`}>
                        <Plus size={12} /> Add Quote
                      </button>
                    )}
                  </div>

                  {showQuoteForm === p.id && (
                    <div className="mb-4">
                      <QuoteForm onAdd={(q) => addQuote(p.id, q)} onCancel={() => setShowQuoteForm(null)} />
                    </div>
                  )}

                  {p.quotes && p.quotes.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-[#0B1020] text-white">
                            <th className="px-3 py-2 text-left">Supplier</th>
                            <th className="px-3 py-2 text-right">Cost Ex VAT</th>
                            <th className="px-3 py-2 text-right">Cost Incl VAT</th>
                            <th className="px-3 py-2 text-center">Lead Time</th>
                            <th className="px-3 py-2 text-center">B-BBEE</th>
                            <th className="px-3 py-2 text-left">Warranty</th>
                            <th className="px-3 py-2 text-left">Payment</th>
                            <th className="px-3 py-2 text-left">Sureties</th>
                            <th className="px-3 py-2 text-center">Selected</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.quotes.map((q, qi) => (
                            <tr key={qi} className={`${qi === p.selected_quote_index ? "bg-[#C8A961]/10 border-l-2 border-l-[#C8A961]" : qi % 2 === 0 ? "bg-white" : "bg-[#FAF8F4]"}`}>
                              <td className="px-3 py-2 font-medium">
                                {q.supplier_name}
                                {q.is_recommended && <span className="ml-1 text-[#C8A961]" title="Recommended">*</span>}
                                {q.tax_clearance && <span className="ml-1 text-[#065F46]" title="Tax clearance valid">&#10003;</span>}
                              </td>
                              <td className="px-3 py-2 text-right font-mono tabular-nums">{formatCurrency(q.cost_ex_vat)}</td>
                              <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold">{formatCurrency(q.cost_incl_vat)}</td>
                              <td className="px-3 py-2 text-center font-mono">{q.lead_time_days} days</td>
                              <td className="px-3 py-2 text-center">{q.bbee_level || "-"}</td>
                              <td className="px-3 py-2">{q.warranty_period || "-"}</td>
                              <td className="px-3 py-2">{q.payment_terms || "-"}</td>
                              <td className="px-3 py-2">{q.sureties || "-"}</td>
                              <td className="px-3 py-2 text-center">
                                {qi === p.selected_quote_index ? (
                                  <CheckCircle size={14} className="mx-auto text-[#065F46]" />
                                ) : p.status === "DRAFT" ? (
                                  <button onClick={() => selectSupplier(p.id, qi, "")} className="text-[#6B7280] hover:text-[#C8A961]" data-testid={`select-quote-${qi}`}>Select</button>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-sm text-[#6B7280] p-4 bg-white rounded-lg border border-dashed border-[#E8E2D5] text-center">
                      No quotes added yet. Add at least one supplier quote.
                    </div>
                  )}

                  {p.selection_rationale && (
                    <div className="mt-2 p-3 rounded-lg bg-white border border-[#E8E2D5]">
                      <span className="text-xs text-[#6B7280]">Selection rationale: </span>
                      <span className="text-sm">{p.selection_rationale}</span>
                    </div>
                  )}
                </div>

                {/* Delivery Checklist (only for approved projects) */}
                {p.status === "APPROVED" && (
                  <DeliveryChecklist
                    checklist={p.delivery_checklist || {}}
                    onUpdate={(cl) => updateChecklist(p.id, cl)}
                    projectStatus={p.status}
                  />
                )}

                {/* Actions */}
                <div className="flex flex-col gap-2 pt-2">
                  {p.status === "DRAFT" && (
                    <>
                      {(p.quotes?.length || 0) < 3 && (
                        <div className="p-3 rounded-lg bg-[#92400E]/10 text-[#92400E] text-xs">
                          <AlertTriangle size={14} className="inline mr-1" />
                          Minimum 3 supplier quotes required for approval. Currently have {p.quotes?.length || 0} of 3.
                        </div>
                      )}
                      {p.quotes?.length >= 3 && p.selected_quote_index !== null && p.selected_quote_index !== undefined && (
                        <button onClick={() => approveProject(p.id)} className="stratum-btn-primary flex items-center gap-2 w-fit" data-testid={`approve-capex-${p.id}`}>
                          <Send size={14} /> Approve & Issue PO
                        </button>
                      )}
                      {p.quotes?.length >= 3 && (p.selected_quote_index === null || p.selected_quote_index === undefined) && (
                        <div className="text-xs text-[#6B7280]">Select a supplier from the quotes above to enable approval.</div>
                      )}
                    </>
                  )}
                  {p.status === "APPROVED" && (
                    <button onClick={() => capitaliseProject(p.id)} className="px-4 py-2 rounded-lg bg-[#C8A961] text-white text-sm font-medium hover:bg-[#B69A51] flex items-center gap-2" data-testid={`capitalise-capex-${p.id}`}>
                      <TrendingUp size={14} /> Capitalise Asset
                    </button>
                  )}
                </div>

                {/* Approvals log */}
                {p.approvals?.length > 0 && (
                  <div>
                    <h5 className="text-xs font-medium text-[#6B7280] uppercase tracking-wider mb-2">Approval History</h5>
                    {p.approvals.map((a, ai) => (
                      <div key={ai} className="flex items-center gap-2 text-xs text-[#6B7280] py-1">
                        <CheckCircle size={12} className="text-[#065F46]" />
                        <span>{a.approved_by_name || "User"} - {a.decision} - {formatDate(a.approved_at)}</span>
                        {a.comment && <span className="text-[#1A1A1A]">"{a.comment}"</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
